import { useMemo, useState } from 'react'

import { useApolloClient } from '@apollo/client'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import {
  FinanceBudgetDetailRecordsDataCollectionDocument,
  ExpenseBudgetMovementFragment,
  FinanceBudgetsDetailBudgetFragment
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import { amountWithCurrency } from 'modules/finance/lib/budgets'
import { expenseStatusVariant, expenseStatusLabel } from 'modules/finance/lib/budgetMovement'

import { createCategoryAsyncFilter } from './filters/categoryFilter'
import { createEmployeeAsyncFilter } from './filters/employeeFilter'
import { RecordDetail } from './RecordDetail'

interface Props {
  budget: FinanceBudgetsDetailBudgetFragment
  locale: string
}

const mapFiltersToGraphqlFilters = (filterValues: Record<string, unknown>) => {
  const employeeId = Array.isArray(filterValues.employeeId) ? filterValues.employeeId : undefined
  const categoryId = Array.isArray(filterValues.categoryId) ? filterValues.categoryId : undefined
  const status = Array.isArray(filterValues.status) ? filterValues.status : undefined

  return {
    ...(employeeId?.length && {
      employeeId: { in: employeeId }
    }),
    ...(categoryId?.length && {
      categoryId: { in: categoryId }
    }),
    ...(status?.length && {
      expensableStatus: { in: status }
    })
  }
}

export const RecordsDataCollection = ({ budget, locale }: Props) => {
  const [selectedItem, setSelectedItem] = useState<ExpenseBudgetMovementFragment | null>(null)
  const { company } = useCurrentSession()
  const apolloClient = useApolloClient()

  const filters = useMemo(
    () => ({
      employeeId: createEmployeeAsyncFilter(apolloClient, budget.id),
      categoryId: createCategoryAsyncFilter(apolloClient, company.id),
      status: {
        label: i18n.t('budgets.detail.tabs.spendings.table.filters.status'),
        type: 'in' as const,
        options: {
          options: [
            { label: i18n.t('expenses_v2.list.filters.draft'), value: 'draft' },
            { label: i18n.t('expenses_v2.list.filters.reversed'), value: 'reversed' },
            { label: i18n.t('expenses_v2.list.filters.pending'), value: 'pending' },
            { label: i18n.t('expenses_v2.list.filters.approved'), value: 'approved' },
            { label: i18n.t('expenses_v2.list.filters.rejected'), value: 'rejected' },
            { label: i18n.t('expenses_v2.list.filters.in_payroll'), value: 'in_payroll' },
            { label: i18n.t('expenses_v2.list.filters.sent_to_pay'), value: 'sent_to_pay' },
            { label: i18n.t('expenses_v2.expense.status.paid'), value: 'paid' }
          ]
        }
      }
    }),
    [apolloClient, budget.id, company.id]
  )

  const source = useGraphqlDataCollectionSource({
    document: FinanceBudgetDetailRecordsDataCollectionDocument,
    mapData: (data): ExpenseBudgetMovementFragment[] => {
      return data.finance.budgetMovementsConnection.edges.map((edge) => edge.node)
    },
    mapPagination: (data) => ({
      total: data.finance.budgetMovementsConnection.totalCount ?? 0
    }),
    mapVariables: ({ filters: filterValues }) => {
      return {
        budgetId: budget.id,
        sortOrder: {
          field: 'effectiveAt',
          order: 'desc' as const
        },
        filter: mapFiltersToGraphqlFilters(filterValues)
      }
    },
    filters,
    itemOnClick: (item: ExpenseBudgetMovementFragment) => () => setSelectedItem(item)
  })

  const getCategoryName = (item: ExpenseBudgetMovementFragment): string => {
    const expensable = item.expensable
    if (!expensable) return '-'
    return (
      expensable.expense?.category?.name ??
      expensable.mileage?.category?.name ??
      expensable.perDiem?.category?.name ??
      '-'
    )
  }

  const visualizations = [
    {
      type: 'table' as const,
      options: {
        columns: [
          {
            label: i18n.t('budgets.detail.tabs.spendings.table.employee'),
            render: (item: ExpenseBudgetMovementFragment) => {
              if (!item.employee) {
                return { type: 'text' as const, value: '-' }
              }
              return {
                type: 'person' as const,
                value: {
                  firstName: item.employee.fullName.split(' ')[0] || '',
                  lastName: item.employee.fullName.split(' ').slice(1).join(' ') || '',
                  src: item.employee.avatar?.url || undefined
                }
              }
            }
          },
          {
            label: i18n.t('budgets.detail.tabs.spendings.table.category'),
            render: (item: ExpenseBudgetMovementFragment) => ({
              type: 'tag' as const,
              value: {
                label: getCategoryName(item)
              }
            })
          },
          {
            label: i18n.t('budgets.detail.tabs.spendings.table.status'),
            render: (item: ExpenseBudgetMovementFragment) => {
              const status = item.expensable?.status
              if (!status) {
                return { type: 'text' as const, value: '-' }
              }
              return {
                type: 'status' as const,
                value: {
                  label: expenseStatusLabel(status),
                  status: expenseStatusVariant(status)
                }
              }
            }
          },
          {
            label: i18n.t('budgets.detail.tabs.spendings.table.document_date'),
            render: (item: ExpenseBudgetMovementFragment) => ({
              type: 'text' as const,
              value: item.effectiveAt ? dateBackendDateTimeToUI(item.effectiveAt) : '-'
            })
          },
          {
            label: i18n.t('budgets.detail.tabs.spendings.table.amount'),
            render: (item: ExpenseBudgetMovementFragment) => ({
              type: 'text' as const,
              value: item.amountCents != null
                ? amountWithCurrency(Number(item.amountCents), item.targetCurrency, locale)
                : '-'
            })
          }
        ]
      }
    }
  ]

  const emptyStates = {
    'no-data': {
      title: i18n.t('budgets.expense_budget_detail.records.empty_state.title'),
      description: i18n.t('budgets.expense_budget_detail.records.empty_state.description'),
      emoji: '📋'
    }
  }

  return (
    <>
      <OneDataCollection
        source={source}
        visualizations={visualizations}
        emptyStates={emptyStates}
      />
      {selectedItem && (
        <RecordDetail item={selectedItem} onClose={() => setSelectedItem(null)} locale={locale} />
      )}
    </>
  )
}
